import { assign, createMachine, sendParent } from 'xstate';
export default createMachine({
    predictableActionArguments: true,
    type: 'parallel',
    schema: {
        events: {}
    },
    id: 'fieldMachine',
    context: {
        initialValue: undefined,
        error: undefined,
        mutable: 'ENABLED',
        name: undefined,
        validators: [],
        value: undefined,
        values: {}
    },
    states: {
        change: {
            initial: 'clean',
            states: {
                clean: {
                    id: 'clean',
                    on: {
                        CHANGE: {
                            target: ['dirty', '#unvalidated'],
                            cond: 'isMutable',
                            actions: ['setValue', 'sendParent']
                        }
                    }
                },
                dirty: {
                    id: 'dirty',
                    on: {
                        CHANGE: {
                            target: '#unvalidated',
                            cond: 'isMutable',
                            actions: ['setValue', 'sendParent']
                        },
                        BLUR: {
                            target: '#validating',
                            cond: 'isReadyForValidation'
                        }
                    }
                }
            }
        },
        mutable: {
            initial: 'initializing',
            states: {
                initializing: {
                    id: 'initMutability',
                    always: [
                        {
                            target: '#enabled',
                            cond: ({ mutable }) => mutable === 'ENABLED'
                        },
                        {
                            target: '#disabled',
                            cond: ({ mutable }) => mutable === 'DISABLED'
                        },
                        {
                            target: '#readonly',
                            cond: ({ mutable }) => mutable === 'READONLY'
                        }
                    ]
                },
                enabled: {
                    id: 'enabled',
                    on: {
                        DISABLE: {
                            target: '#disabled'
                        },
                        READONLY: {
                            target: '#readonly'
                        }
                    }
                },
                disabled: {
                    id: 'disabled',
                    on: {
                        ENABLE: {
                            target: '#enabled'
                        },
                        READONLY: {
                            target: '#readonly'
                        }
                    }
                },
                readonly: {
                    id: 'readonly',
                    on: {
                        ENABLE: {
                            target: '#enabled'
                        },
                        DISABLE: {
                            target: '#disabled'
                        }
                    }
                }
            }
        },
        validation: {
            initial: 'unvalidated',
            states: {
                unvalidated: {
                    id: 'unvalidated',
                    on: {
                        VALIDATE: {
                            target: '#validating'
                        }
                    }
                },
                validating: {
                    id: 'validating',
                    invoke: {
                        id: 'validate',
                        src: ({ validators, value, values }) => async () => {
                            // eslint-disable-next-line no-restricted-syntax
                            for (const validate of validators) {
                                // eslint-disable-next-line no-await-in-loop
                                await validate(value, values);
                            }
                        },
                        onDone: {
                            target: ['#dirty', '#valid']
                        },
                        onError: {
                            target: ['#dirty', '#invalid'],
                            actions: 'setError'
                        }
                    }
                },
                valid: {
                    id: 'valid',
                    on: {
                        /*
                         * We have to include fields that have been already validated b/c fields that are in
                         * the `valid` state may have been validated against stale values (ex: if it was
                         * considered valid in the past due to a previously entered value of a different field)
                         */
                        VALIDATE: {
                            target: 'validating'
                        },
                        /*
                         * On submit, send back to the `clean` state and assign `initialValue` to the sanitized
                         * `value`
                         */
                        COMMIT: {
                            target: '#clean',
                            actions: assign({
                                initialValue: ({ value }) => {
                                    return value;
                                }
                            })
                        }
                    }
                },
                invalid: {
                    id: 'invalid',
                    on: {
                        VALIDATE: {
                            target: 'validating'
                        }
                    }
                }
            }
        }
    },
    on: {
        SIBLING_CHANGE: {
            actions: assign({
                values: (context, { values }) => values
            })
        },
        RESET: {
            target: '#clean',
            actions: assign({
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                error: (_) => undefined,
                value: ({ initialValue }) => initialValue
            })
        },
        FREEZE: {
            target: '#readonly'
        },
        UNFREEZE: {
            target: '#initMutability'
        }
    }
}, {
    guards: {
        isMutable: (context, event, states) => {
            return (states.state.matches('mutable.enabled') &&
                !states.state.matches('validation.validating'));
        },
        isReadyForValidation: (context, event, states) => {
            return (states.state.matches('mutable.enabled') &&
                states.state.matches('validation.unvalidated'));
        }
    },
    actions: {
        setError: assign({
            error: (context, { data }) => {
                return data.message;
            }
        }),
        setValue: assign({
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            error: (_) => undefined,
            value: (_, { value }) => {
                return value;
            }
        }),
        sendParent: sendParent(({ name }, { type, value }) => {
            return {
                type,
                name,
                value
            };
        })
    }
});
